/**
 * Portal: the page the emailed link opens, where the patron chooses their password.
 *
 * The token is in the URL fragment (#…): browsers never send it to the server
 * nor in the Referer header. It is removed from the address bar once read.
 */

import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  AccountAccessError,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  choosePassword,
  inspectLink,
  type LinkInfo,
} from "../account-access/accountAccessApi";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white";

type LinkState =
  | { status: "checking" }
  | { status: "valid"; info: LinkInfo }
  | { status: "invalid"; message: string };

/** Read once: the address bar is cleaned right after. */
function readToken(): string {
  return window.location.hash.replace(/^#/, "");
}

export function AccountAccessPasswordPage() {
  const navigate = useNavigate();
  const id = useId();
  const [token] = useState(readToken);
  const [link, setLink] = useState<LinkState>(
    token ? { status: "checking" } : { status: "invalid", message: "Ce lien est incomplet." }
  );
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!token) return;
    // Keep the token out of the history and of anything that reads the URL
    window.history.replaceState(window.history.state, "", window.location.pathname);

    let cancelled = false;
    inspectLink(token)
      .then((info) => {
        if (!cancelled) setLink({ status: "valid", info });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLink({
            status: "invalid",
            message: e instanceof Error ? e.message : "Ce lien n'est plus valable.",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choisissez un mot de passe d'au moins ${String(MIN_PASSWORD_LENGTH)} caractères.`);
      return;
    }
    if (password !== confirmation) {
      setError("Les deux mots de passe ne sont pas identiques.");
      return;
    }

    setError(null);
    setPending(true);
    try {
      const { message } = await choosePassword(token, password);
      void navigate("/opac/login", { replace: true, state: { notice: message } });
    } catch (e) {
      if (e instanceof AccountAccessError && e.status === 410) {
        setLink({ status: "invalid", message: e.message });
      } else {
        setError(e instanceof Error ? e.message : "Une erreur est survenue.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {link.status === "checking" && (
          <p role="status" className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
            Vérification du lien…
          </p>
        )}

        {link.status === "invalid" && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lien non valable</h1>
            <p
              role="alert"
              className="flex gap-2 rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
            >
              <AlertCircle className="h-5 w-5 shrink-0" aria-hidden />
              {link.message}
            </p>
            <Link
              to="/opac/acces-compte"
              className="inline-block font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Recevoir un nouveau lien
            </Link>
          </div>
        )}

        {link.status === "valid" && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {link.info.activation ? "Activez votre compte" : "Nouveau mot de passe"}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Bonjour {link.info.firstName}, choisissez votre mot de passe pour le portail. Vous
              vous connecterez ensuite avec votre numéro de carte et ce mot de passe.
            </p>

            <form onSubmit={(e) => void submit(e)} noValidate className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor={`${id}-password`}
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Mot de passe
                </label>
                <input
                  id={`${id}-password`}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  autoComplete="new-password"
                  autoFocus
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  maxLength={MAX_PASSWORD_LENGTH}
                  aria-describedby={`${id}-hint`}
                  className={inputClass}
                />
                <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Au moins {MIN_PASSWORD_LENGTH} caractères. Une phrase facile à retenir convient
                  très bien.
                </p>
              </div>

              <div>
                <label
                  htmlFor={`${id}-confirmation`}
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Confirmez le mot de passe
                </label>
                <input
                  id={`${id}-confirmation`}
                  type={showPassword ? "text" : "password"}
                  value={confirmation}
                  onChange={(e) => {
                    setConfirmation(e.target.value);
                  }}
                  autoComplete="new-password"
                  required
                  maxLength={MAX_PASSWORD_LENGTH}
                  className={inputClass}
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => {
                    setShowPassword(e.target.checked);
                  }}
                  className="h-4 w-4 accent-blue-600"
                />
                Afficher les mots de passe
              </label>

              {error && (
                <p
                  role="alert"
                  className="rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-wait disabled:opacity-70 dark:focus:ring-offset-gray-800"
              >
                {pending ? "Enregistrement…" : "Enregistrer mon mot de passe"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default AccountAccessPasswordPage;
